import * as React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import {EpiImage} from "features/search/types";
import {truncate} from 'features/services/utils/string-helpers';
import {ImageSizes} from 'components/organisms/Shell/image-size-enum';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {getLocatizedDateWithFullMonth} from 'features/services/utils/date-helpers';
import { HeadlineFont, HeadlineLevel, HeadlineSize, LinkStyle, Margin, ParagraphSize, TextColor } from 'features/ui/enums';

import './style.scss';

import {A} from 'components/atoms/LinksAndButtons/A';
import {P} from 'components/atoms/Text/P';
import {Headline} from 'components/atoms/Text/Headline';
import {MainGrid} from 'components/atoms/Layout/MainGrid';
import {ReadMore} from 'components/atoms/LinksAndButtons/ReadMore';
import {AsyncImage} from 'components/atoms/Media/AsyncImage';
import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';

interface Props {
    itemType: BlogItemType;
    singleFeed?: boolean;
    parentName?: string;
    image: EpiImage;
    headline: string;
    text: string;
    link: string;
    date: string;
}

export enum BlogItemType {
    GRIDSMALL = 'grid-small',
    GRIDMEDIUM = 'grid-medium',
    GRIDLARGE = 'grid-large',
    LISTLARGE = 'list-large',
    LISTSMALL = 'list-small',
    SLIDER = 'slider',
}

export const BlogItem: React.FunctionComponent<Props> = ({
    parentName, image, headline, text, link, date,
    singleFeed, itemType
}) => {
    const isMobile = useDeviceStore(selectIsMobile);
    const itemClasses = classnames('blog-item', {
        'blog-item--has-bg': itemType !== BlogItemType.GRIDSMALL || isMobile,
        
        'blog-item--grid-large': itemType === BlogItemType.GRIDLARGE,
        'blog-item--grid-medium': itemType === BlogItemType.GRIDMEDIUM,
        'blog-item--grid-small': itemType === BlogItemType.GRIDSMALL,
        'blog-item--list-small': itemType === BlogItemType.LISTSMALL,
        'blog-item--slider': itemType === BlogItemType.SLIDER,
    });
    const largeParentName = itemType !== BlogItemType.GRIDSMALL && itemType !== BlogItemType.LISTSMALL;
    
    const renderImage = () => {
        switch (itemType) {
            case BlogItemType.SLIDER:
            case BlogItemType.GRIDMEDIUM:
            case BlogItemType.LISTSMALL:
                return <AsyncImage
                    fullHeight
                    className="blog-item__image"
                    src={image.url}
                    quality={ImageSizes.SIZE_600}
                    alt={image.altText}
                />;
            case BlogItemType.GRIDLARGE:
            case BlogItemType.LISTLARGE:
                return <AsyncImage
                    fullWidth
                    className="blog-item__image"
                    src={image.url}
                    quality={ImageSizes.SIZE_900}
                    alt={image.altText}
                />;
            default:
                return;
        }
    };
    const renderText = () => {
        switch (itemType) {
            case BlogItemType.LISTLARGE:
                return <>
                    <Headline 
                        level={HeadlineLevel.H3} 
                        className="blog-item__headline" 
                    >
                        {headline}
                    </Headline>
                    <P 
                        color={TextColor.MUTED} 
                        margin={Margin.S} 
                        className="blog-date"
                    >
                        {getLocatizedDateWithFullMonth(date)}
                    </P>
                    <P
                        margin={Margin.S} 
                    >
                        {truncate(text, 15)}
                    </P>
                </>;

            case BlogItemType.GRIDMEDIUM:
            case BlogItemType.GRIDLARGE:
                return <>
                    <Headline 
                        font={HeadlineFont.SERIF}
                        level={HeadlineLevel.H3} 
                        size={HeadlineSize.S} 
                        className="blog-item__headline" 
                    >
                        {headline}
                    </Headline>
                    <P 
                        color={TextColor.MUTED} 
                        margin={Margin.S} 
                        className="blog-date"
                    >
                        {getLocatizedDateWithFullMonth(date)}
                    </P>
                    <P
                        margin={Margin.S} 
                    >
                        {truncate(text, 15)}
                    </P>
                </>;

            case BlogItemType.SLIDER:
                return <>
                    <Headline 
                        font={HeadlineFont.SERIF}
                        level={HeadlineLevel.H3} 
                        size={HeadlineSize.S} 
                        className="blog-item__headline" 
                    >
                        {headline}
                    </Headline>
                    <P 
                        color={TextColor.MUTED} 
                        margin={Margin.S} 
                        size={ParagraphSize.S} 
                        className="blog-date"
                    >
                        {getLocatizedDateWithFullMonth(date)}
                    </P>
                    <P
                        margin={Margin.S} 
                    >
                        {truncate(text, 15)}
                    </P>
                </>;

            case BlogItemType.LISTSMALL:
                return <>
                    <Headline 
                        level={HeadlineLevel.H3} 
                        size={HeadlineSize.XS} 
                        className="blog-item__headline" 
                    >
                        {headline}
                    </Headline>
                    <P 
                        color={TextColor.MUTED} 
                        margin={Margin.S} 
                        className="blog-date"
                    >
                        {getLocatizedDateWithFullMonth(date)}
                    </P>
                    <P
                        margin={Margin.S} 
                    >
                        {truncate(text, 15)}
                    </P>
                </>;

            case BlogItemType.GRIDSMALL:
                return <>
                    <Headline 
                        font={HeadlineFont.SERIF}
                        level={HeadlineLevel.H3} 
                        size={HeadlineSize.XS} 
                        className="blog-item__headline" 
                    >
                        {headline}
                    </Headline>
                    <P 
                        color={TextColor.MUTED} 
                        margin={Margin.S} 
                        size={ParagraphSize.S} 
                        className="blog-date"
                    >
                        {getLocatizedDateWithFullMonth(date)}
                    </P>
                </>;

            default:
                return;
        }
    };
    const renderContent = () => {
        return (
            <MainGrid noGap>
                {image && renderImage()}
                <div className="blog-item__text">
                    {parentName && !singleFeed &&
                        <P 
                            color={TextColor.MUTED} 
                            margin={Margin.S} 
                            size={largeParentName 
                                ? ParagraphSize.M 
                                : undefined
                            } 
                            className="blog-item__parent"
                        >
                            {parentName}
                        </P>
                    }
                    {renderText()}

                    <div tabIndex={-1} aria-hidden="true">
                        <ReadMore
                            text={getFormattedMessage('jm.components.BlogItem.readMore')}
                        />
                    </div>
                    <A
                        variant={LinkStyle.UNSTYLED}
                        route
                        href={link} 
                        className="blog-item__link" 
                        title={getFormattedMessage('jm.components.BlogItem.readMoreWithHeadline', {headline: headline})} 
                    >
                        <span className='visually-hidden'>
                            <FormattedMessage id="jm.components.BlogItem.readMoreWithHeadline" values={{headline: headline}} />
                        </span>
                    </A>
                </div>
            </MainGrid>
        )
    };

    if(itemType === BlogItemType.SLIDER) return (
        <article className={itemClasses}>
            {renderContent()}
        </article>
    );
    return (
        <li className={itemClasses}>
            <article>
                {renderContent()}
            </article>
        </li>
    )
};
